import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import PageTitle from 'components/pageTitle';
import Button from 'components/ui/Button';
import {
  Home,
  Search,
  FileText,
  Users,
  CheckSquare,
  ListTodo,
  BookOpen,
  Clock,
  CheckCircle,
  AlertCircle,
  BarChart3,
  RefreshCw,
  TrendingUp,
  Target,
  Award,
  Zap,
  ArrowRight,
  Activity,
  PieChart,
  Calendar,
  Star,
  Briefcase
} from 'lucide-react';
import { useAuth } from 'contexts/AuthContext';
import { useToast } from 'contexts/ToastContext';
import { getInternalLegalResearchRequestsByProsecutor } from 'services/legalResearchService';
import { getLegislativeDraftingsByStakeHolderID } from 'services/legislativeService';
import { getLegalAdvisoriesByProsecutor } from 'services/legalAdvisoryService';
import { getSectorDirectives } from 'services/sectorDirectives';
import { getRegisterDirectivesByProsecutor } from 'services/registerDirectivesService';
import { getLegalTrainingProvisionsByProsecutor } from 'services/legalTrainingProvision';
import {
  LEGAL_RESEARCH_REQUEST_STATUS,
  LEGISLATIVE_DRAFTING_REQUEST_STATUS,
  LEGAL_ADVISORY_REQUEST_STATUS,
  SECTOR_DIRECTIVES_REQUEST_STATUS,
  LEGAL_TRAINING_PROVISION_REQUEST_STATUS
} from 'utils/constantValues';

// Constants
const ACTIVE_KEYWORDS = [
  'received', 'pending', 'underway', 'in progress', 'under review',
  'under analysis', 'submitted', 'submitted by stakeholder', 'submitted internally',
  'oral instruction logged', 'proposal submitted', 'proposal underway',
  'draft prepared', 'team appointed', 'identification in progress',
  'audit in progress', 'summary submitted', 'session scheduled',
  'team assigned', 'training manual submitted', 'training scheduled',
  'assigned to prosecutor', 'forwarded to', 'sent to'
];

const COMPLETED_KEYWORDS = [
  'completed', 'published', 'final', 'archived', 'closed',
  'finalization complete', 'audit complete', 'final approval',
  'reference number submitted', 'letter reference number submitted'
];

const PENDING_REVIEW_KEYWORDS = [
  'revision', 'revision requested', 'revision updated', 'feedback',
  'awaiting approval', 'draft approved', 'proposal revision',
  'proposal update', 'draft - updated', 'proposal presentation feedback submitted',
  'additional info', 'waiting for', 'back to', 'reviewed by',
  'approved by head', 'follow-up'
];

// Helper functions
const isActiveStatus = (label: string | undefined | null): boolean => {
  if (!label) return false;
  const lowerLabel = label.toLowerCase();
  return ACTIVE_KEYWORDS.some(keyword => lowerLabel.includes(keyword));
};

const isCompletedStatus = (label: string | undefined | null): boolean => {
  if (!label) return false;
  const lowerLabel = label.toLowerCase();
  return COMPLETED_KEYWORDS.some(keyword => lowerLabel.includes(keyword));
};

const isPendingReviewStatus = (label: string | undefined | null): boolean => {
  if (!label) return false;
  const lowerLabel = label.toLowerCase();
  return PENDING_REVIEW_KEYWORDS.some(keyword => lowerLabel.includes(keyword));
};

const getStatusMapForService = (service: string) => {
  const statusMaps = {
    legalResearch: { map: LEGAL_RESEARCH_REQUEST_STATUS, color: 'blue' },
    legislativeDrafting: { map: LEGISLATIVE_DRAFTING_REQUEST_STATUS, color: 'purple' },
    legalAdvisory: { map: LEGAL_ADVISORY_REQUEST_STATUS, color: 'teal' },
    sectorDirectives: { map: SECTOR_DIRECTIVES_REQUEST_STATUS, color: 'amber' },
    registerDirectives: { map: SECTOR_DIRECTIVES_REQUEST_STATUS, color: 'slate' },
    legalTraining: { map: LEGAL_TRAINING_PROVISION_REQUEST_STATUS, color: 'rose' }
  };
  return statusMaps[service as keyof typeof statusMaps] || null;
};

const getAuthToken = (): string | null => {
  try {
    const tokenData = sessionStorage.getItem("authToken") || localStorage.getItem("authToken");
    if (!tokenData) return null;
    try {
      const parsed = JSON.parse(tokenData);
      return parsed.access || parsed.access_token || parsed.token;
    } catch {
      return tokenData;
    }
  } catch (error) {
    console.error("Error retrieving auth token:", error);
    return null;
  }
};

// Types
interface ServiceStats {
  active: number;
  completed: number;
  pendingReview: number;
}

interface ServiceDataItem {
  count: number;
  stats: ServiceStats;
}

interface ServiceData {
  legalResearch: ServiceDataItem;
  legislativeDrafting: ServiceDataItem;
  legalAdvisory: ServiceDataItem;
  sectorDirectives: ServiceDataItem;
  registerDirectives: ServiceDataItem;
  legalTraining: ServiceDataItem;
}

interface ServiceConfig {
  key: keyof ServiceData;
  title: string;
  icon: React.ReactNode;
  iconColor: string;
  bgColor: string;
  borderColor: string;
  badgeColor: string;
  description: string;
}

// Service Configuration
const SERVICE_CONFIGS: ServiceConfig[] = [
  {
    key: 'legalResearch',
    title: 'Conduct Legal Research',
    icon: <Search className="w-5 h-5" />,
    iconColor: 'text-blue-600',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-100',
    badgeColor: 'bg-blue-100 text-blue-700',
    description: 'Legal research requests and analysis'
  },
  {
    key: 'legislativeDrafting',
    title: 'Legislative Drafting',
    icon: <FileText className="w-5 h-5" />,
    iconColor: 'text-purple-600',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-100',
    badgeColor: 'bg-purple-100 text-purple-700',
    description: 'Drafting of legislative documents'
  },
  {
    key: 'legalAdvisory',
    title: 'Legal Advisory',
    icon: <Users className="w-5 h-5" />,
    iconColor: 'text-teal-600',
    bgColor: 'bg-teal-50',
    borderColor: 'border-teal-100',
    badgeColor: 'bg-teal-100 text-teal-700',
    description: 'Legal opinions and consultations'
  },
  {
    key: 'sectorDirectives',
    title: 'Sector Directives',
    icon: <CheckSquare className="w-5 h-5" />,
    iconColor: 'text-amber-600',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-100',
    badgeColor: 'bg-amber-100 text-amber-700',
    description: 'Accuracy verification of sector directives'
  },
  {
    key: 'registerDirectives',
    title: 'Register Directives',
    icon: <ListTodo className="w-5 h-5" />,
    iconColor: 'text-slate-600',
    bgColor: 'bg-slate-50',
    borderColor: 'border-slate-100',
    badgeColor: 'bg-slate-100 text-slate-700',
    description: 'Directive registration and management'
  },
  {
    key: 'legalTraining',
    title: 'Legal Training',
    icon: <BookOpen className="w-5 h-5" />,
    iconColor: 'text-rose-600',
    bgColor: 'bg-rose-50',
    borderColor: 'border-rose-100',
    badgeColor: 'bg-rose-100 text-rose-700',
    description: 'Legal training programs and provisions'
  }
];

const LegalResearchProsecutorDashboard: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { showToast } = useToast();
  
  const [serviceData, setServiceData] = useState<ServiceData>({
    legalResearch: { count: 0, stats: { active: 0, completed: 0, pendingReview: 0 } },
    legislativeDrafting: { count: 0, stats: { active: 0, completed: 0, pendingReview: 0 } },
    legalAdvisory: { count: 0, stats: { active: 0, completed: 0, pendingReview: 0 } },
    sectorDirectives: { count: 0, stats: { active: 0, completed: 0, pendingReview: 0 } },
    registerDirectives: { count: 0, stats: { active: 0, completed: 0, pendingReview: 0 } },
    legalTraining: { count: 0, stats: { active: 0, completed: 0, pendingReview: 0 } }
  });
  
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const calculateStatsForPeriod = useCallback((data: any[]): ServiceStats => {
    let active = 0, completed = 0, pendingReview = 0;
    
    data.forEach(item => {
      const statusValue = item.status || item.current_status || item.request_status;
      const result = getStatusMapForService(item.service);
      
      if (result?.map) {
        const statusDef = Object.values(result.map).find((s: any) => s.value === statusValue);
        if (statusDef) {
          if (isActiveStatus(statusDef.label)) active++;
          else if (isCompletedStatus(statusDef.label)) completed++;
          else if (isPendingReviewStatus(statusDef.label)) pendingReview++;
        }
      }
    });
    
    return { active, completed, pendingReview };
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const prosecutorUserId = user?.user_id || user?.id;
      const token = getAuthToken();
      
      if (!prosecutorUserId) {
        showToast(t("NoUserInfo") || "Unable to identify user", "error");
        setLoading(false);
        return;
      }

      const [
        researchData,
        draftingData,
        advisoryData,
        sectorData,
        registerData,
        trainingData
      ] = await Promise.allSettled([
        getInternalLegalResearchRequestsByProsecutor(token || '', prosecutorUserId.toString()),
        getLegislativeDraftingsByStakeHolderID(prosecutorUserId.toString()),
        getLegalAdvisoriesByProsecutor(prosecutorUserId.toString()),
        getSectorDirectives(token || ''),
        getRegisterDirectivesByProsecutor(token || '', prosecutorUserId.toString()),
        getLegalTrainingProvisionsByProsecutor(prosecutorUserId.toString())
      ]);

      const processData = (data: any, service: string) => {
        return data.status === 'fulfilled' && Array.isArray(data.value)
          ? data.value.map((item: any) => ({ ...item, service }))
          : [];
      };

      const legalResearchItems = processData(researchData, 'legalResearch');
      const legislativeDraftingItems = processData(draftingData, 'legislativeDrafting');
      const legalAdvisoryItems = processData(advisoryData, 'legalAdvisory');
      const sectorDirectivesItems = processData(sectorData, 'sectorDirectives');
      const registerDirectivesItems = processData(registerData, 'registerDirectives');
      const legalTrainingItems = processData(trainingData, 'legalTraining');

      setServiceData({
        legalResearch: { count: legalResearchItems.length, stats: calculateStatsForPeriod(legalResearchItems) },
        legislativeDrafting: { count: legislativeDraftingItems.length, stats: calculateStatsForPeriod(legislativeDraftingItems) },
        legalAdvisory: { count: legalAdvisoryItems.length, stats: calculateStatsForPeriod(legalAdvisoryItems) },
        sectorDirectives: { count: sectorDirectivesItems.length, stats: calculateStatsForPeriod(sectorDirectivesItems) },
        registerDirectives: { count: registerDirectivesItems.length, stats: calculateStatsForPeriod(registerDirectivesItems) },
        legalTraining: { count: legalTrainingItems.length, stats: calculateStatsForPeriod(legalTrainingItems) }
      });
    } catch (error) {
      console.error('Error fetching data:', error);
      showToast(t("ErrorLoadingData") || "Error loading data", "error");
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, [user, showToast, t, calculateStatsForPeriod]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRefresh = () => {
    setIsRefreshing(true);
    fetchData();
  };

  // Calculate aggregated statistics
  const totalRequests = Object.values(serviceData).reduce((acc, s) => acc + s.count, 0);
  const totalActive = Object.values(serviceData).reduce((acc, s) => acc + s.stats.active, 0);
  const totalCompleted = Object.values(serviceData).reduce((acc, s) => acc + s.stats.completed, 0);
  const totalPendingReview = Object.values(serviceData).reduce((acc, s) => acc + s.stats.pendingReview, 0);
  const completionRate = totalRequests > 0 ? Math.round((totalCompleted / totalRequests) * 100) : 0;
  
  // Calculate productivity score
  const productivityScore = totalRequests > 0 ? Math.round((totalCompleted / totalRequests) * 100) : 0;

  // Get current date
  const currentDate = new Date().toLocaleDateString('en-US', { 
    weekday: 'long', 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });

  // Stats Cards Data
  const statCards = [
    { label: 'Total Requests', value: totalRequests, icon: <Briefcase className="w-4 h-4" />, color: 'text-slate-600' },
    { label: 'Active', value: totalActive, icon: <Activity className="w-4 h-4" />, color: 'text-amber-600' },
    { label: 'Completed', value: totalCompleted, icon: <CheckCircle className="w-4 h-4" />, color: 'text-emerald-600' },
    { label: 'Pending', value: totalPendingReview, icon: <AlertCircle className="w-4 h-4" />, color: 'text-rose-600' }
  ];

  // Recent activity data (mock - replace with actual data)
  const recentActivities = [
    { id: 1, type: 'legalResearch', action: 'New research request', time: '2 hours ago', status: 'pending' },
    { id: 2, type: 'legalAdvisory', action: 'Advisory completed', time: '5 hours ago', status: 'completed' },
    { id: 3, type: 'legislativeDrafting', action: 'Draft under review', time: '1 day ago', status: 'active' },
    { id: 4, type: 'legalTraining', action: 'Training scheduled', time: '2 days ago', status: 'active' },
    { id: 5, type: 'legalResearch', action: 'Research findings submitted', time: '3 days ago', status: 'completed' }
  ];

  return (
    <div className="bg-slate-50 min-h-screen">
      {/* Full width header with background */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="px-6 py-4">
          <PageTitle
            text={t("LegalResearchProsecutorDashboard") || "Legal Services Dashboard"}
            icon={<Home />}
            iconBgColor="bg-slate-800"
            iconColor="text-white"
            subtitle={t("LegalResearchProsecutorDashboardSubtitle") || "Comprehensive overview of all assigned legal services and requests"}
          />
        </div>
      </div>

      {/* Main Content - Full Width */}
      <div className="px-6 py-6">
        {/* Two Column Layout - Full Width */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          
          {/* LEFT COLUMN - Main Content (3/4 width) */}
          <div className="lg:col-span-3 space-y-6">
            
            {/* Quick Stats Row - Full width cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {statCards.map((stat, idx) => (
                <div key={idx} className="bg-white rounded-lg border border-slate-200 p-5 hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-medium text-slate-500">{stat.label}</span>
                    <div className={`p-2 rounded-lg ${stat.color === 'text-slate-600' ? 'bg-slate-100' : 
                      stat.color === 'text-amber-600' ? 'bg-amber-50' :
                      stat.color === 'text-emerald-600' ? 'bg-emerald-50' : 'bg-rose-50'}`}>
                      <span className={stat.color}>{stat.icon}</span>
                    </div>
                  </div>
                  <p className="text-3xl font-bold text-slate-900">
                    {loading ? '—' : stat.value.toLocaleString()}
                  </p>
                  <div className="mt-2">
                    <span className="text-xs text-slate-400">vs last month</span>
                    <TrendingUp className="inline-block w-3 h-3 ml-1 text-emerald-500" />
                  </div>
                </div>
              ))}
            </div>

            {/* Services Grid - 3 columns for full width */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-slate-100 rounded-lg">
                    <Users className="w-4 h-4 text-slate-600" />
                  </div>
                  <h3 className="text-base font-semibold text-slate-900">Legal Services Portfolio</h3>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRefresh}
                  loading={isRefreshing}
                  leftIcon={<RefreshCw className="w-3 h-3" />}
                  className="border-slate-200 hover:bg-slate-50 text-slate-700 text-xs h-8"
                >
                  Refresh All
                </Button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                {SERVICE_CONFIGS.map((config) => {
                  const data = serviceData[config.key];
                  const total = data.count;
                  const { active, completed, pendingReview } = data.stats;
                  const completionPercent = total > 0 ? Math.round((completed / total) * 100) : 0;

                  return (
                    <div
                      key={config.key}
                      className="group bg-white rounded-lg border border-slate-200 hover:border-slate-300 hover:shadow-md transition-all duration-200 overflow-hidden"
                    >
                      <div className="p-5">
                        <div className="flex items-start gap-3 mb-4">
                          <div className={`p-2.5 ${config.bgColor} rounded-lg`}>
                            <span className={config.iconColor}>{config.icon}</span>
                          </div>
                          <div className="flex-1">
                            <h4 className="font-semibold text-slate-900 text-sm">{config.title}</h4>
                            <p className="text-slate-500 text-xs mt-0.5">{config.description}</p>
                          </div>
                        </div>

                        <div className="flex items-end justify-between mb-3">
                          <div>
                            <span className="text-3xl font-bold text-slate-900">
                              {loading ? '—' : total}
                            </span>
                            <span className="text-sm text-slate-500 ml-1.5">
                              {config.key === 'legalTraining' ? 'programs' : 'requests'}
                            </span>
                          </div>
                          <div className="text-right">
                            <span className="text-xs text-slate-500">Progress</span>
                            <p className="text-base font-semibold text-slate-700">{completionPercent}%</p>
                          </div>
                        </div>

                        <div className="w-full bg-slate-100 rounded-full h-1.5 mb-4">
                          <div 
                            className="bg-emerald-500 h-1.5 rounded-full transition-all duration-500"
                            style={{ width: `${completionPercent}%` }}
                          />
                        </div>

                        <div className="grid grid-cols-3 gap-3 pt-2">
                          <div className="text-center">
                            <p className="text-xs text-slate-500 mb-1">Active</p>
                            <p className="text-lg font-semibold text-slate-900">{loading ? '—' : active}</p>
                          </div>
                          <div className="text-center">
                            <p className="text-xs text-slate-500 mb-1">Completed</p>
                            <p className="text-lg font-semibold text-emerald-600">{loading ? '—' : completed}</p>
                          </div>
                          <div className="text-center">
                            <p className="text-xs text-slate-500 mb-1">Review</p>
                            <p className="text-lg font-semibold text-rose-600">{loading ? '—' : pendingReview}</p>
                          </div>
                        </div>

                        {/* Status Indicators */}
                        {!loading && total > 0 && (pendingReview > 0 || active > 0) && (
                          <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-slate-100">
                            {active > 0 && (
                              <span className={`inline-flex items-center gap-1 px-2 py-1 ${config.bgColor} rounded-md text-xs font-medium ${config.iconColor}`}>
                                <Clock className="w-3 h-3" />
                                {active} in progress
                              </span>
                            )}
                            {pendingReview > 0 && (
                              <span className="inline-flex items-center gap-1 px-2 py-1 bg-rose-50 text-rose-600 rounded-md text-xs font-medium">
                                <AlertCircle className="w-3 h-3" />
                                {pendingReview} for review
                              </span>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Footer */}
                      <div className="px-5 py-3 bg-slate-50 border-t border-slate-100">
                        <button className="w-full flex items-center justify-center gap-2 text-sm text-slate-600 hover:text-slate-900 transition-colors">
                          View Details
                          <ArrowRight className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN - Performance & Analytics (1/4 width) */}
          <div className="lg:col-span-1 space-y-6">
            
            {/* Welcome Card */}
            <div className="bg-gradient-to-r from-slate-800 to-slate-700 rounded-lg p-5 text-white">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-white/10 rounded-lg">
                  <Calendar className="w-4 h-4 text-white" />
                </div>
                {/* <div>
                  <p className="text-xs text-white/70">Welcome back,</p>
                  <p className="text-sm font-semibold text-white">{user?.name || 'Prosecutor'}</p>
                </div> */}
              </div>
              <p className="text-xs text-white/60">{currentDate}</p>
            </div>

            {/* Performance Score Circle */}
            <div className="bg-white rounded-lg border border-slate-200 p-5 text-center">
              <div className="relative inline-flex items-center justify-center mb-3">
                <svg className="w-28 h-28 transform -rotate-90">
                  <circle
                    cx="56"
                    cy="56"
                    r="52"
                    stroke="currentColor"
                    strokeWidth="8"
                    fill="none"
                    className="text-slate-100"
                  />
                  <circle
                    cx="56"
                    cy="56"
                    r="52"
                    stroke="currentColor"
                    strokeWidth="8"
                    fill="none"
                    strokeDasharray={`${2 * Math.PI * 52}`}
                    strokeDashoffset={`${2 * Math.PI * 52 * (1 - productivityScore / 100)}`}
                    className="text-emerald-500 transition-all duration-1000"
                  />
                </svg>
                <div className="absolute">
                  <Star className="w-8 h-8 text-emerald-500 mx-auto mb-1" />
                  <p className="text-2xl font-bold text-slate-900">{productivityScore}%</p>
                </div>
              </div>
              <p className="text-sm font-medium text-slate-900">Productivity Score</p>
              <p className="text-xs text-slate-500 mt-1">Based on completed tasks</p>
            </div>

            {/* Quick Stats Summary */}
            <div className="bg-white rounded-lg border border-slate-200 p-5">
              <h3 className="text-sm font-semibold text-slate-900 mb-4">Performance Overview</h3>
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-slate-600">Completion Rate</span>
                    <span className="font-medium text-slate-900">{completionRate}%</span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-2">
                    <div 
                      className="bg-emerald-500 h-2 rounded-full transition-all duration-500"
                      style={{ width: `${completionRate}%` }}
                    />
                  </div>
                </div>

                <div className="space-y-3 pt-2">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <Zap className="w-3 h-3 text-amber-500" />
                      <span className="text-xs text-slate-600">Active Tasks</span>
                    </div>
                    <span className="text-sm font-semibold text-slate-900">{loading ? '—' : totalActive}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <Award className="w-3 h-3 text-emerald-500" />
                      <span className="text-xs text-slate-600">Completed</span>
                    </div>
                    <span className="text-sm font-semibold text-slate-900">{loading ? '—' : totalCompleted}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <Target className="w-3 h-3 text-rose-500" />
                      <span className="text-xs text-slate-600">Needs Review</span>
                    </div>
                    <span className="text-sm font-semibold text-slate-900">{loading ? '—' : totalPendingReview}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-white rounded-lg border border-slate-200">
              <div className="px-5 py-4 border-b border-slate-100">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-slate-100 rounded-lg">
                    <Clock className="w-4 h-4 text-slate-600" />
                  </div>
                  <h3 className="text-sm font-semibold text-slate-900">Recent Activity</h3>
                </div>
              </div>
              <div className="p-4 space-y-3">
                {recentActivities.map((activity) => (
                  <div key={activity.id} className="flex items-start gap-3 p-2 rounded-lg hover:bg-slate-50 transition-colors">
                    <div className={`p-1.5 rounded-lg flex-shrink-0 ${
                      activity.status === 'completed' ? 'bg-emerald-50' : 
                      activity.status === 'pending' ? 'bg-rose-50' : 'bg-amber-50'
                    }`}>
                      {activity.status === 'completed' ? (
                        <CheckCircle className="w-3 h-3 text-emerald-600" />
                      ) : activity.status === 'pending' ? (
                        <AlertCircle className="w-3 h-3 text-rose-600" />
                      ) : (
                        <Clock className="w-3 h-3 text-amber-600" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-slate-900 truncate">{activity.action}</p>
                      <p className="text-xs text-slate-500">{activity.time}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-lg border border-slate-200">
              <div className="px-5 py-4 border-b border-slate-100">
                <h3 className="text-sm font-semibold text-slate-900">Quick Actions</h3>
              </div>
              <div className="p-3 space-y-1">
                <button className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 rounded-lg transition-colors">
                  + New Legal Research
                </button>
                <button className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 rounded-lg transition-colors">
                  + Create Legislative Draft
                </button>
                <button className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 rounded-lg transition-colors">
                  + Request Legal Advisory
                </button>
                <button className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 rounded-lg transition-colors">
                  + Schedule Training
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LegalResearchProsecutorDashboard;